/* eslint-disable arrow-body-style */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Autocomplete,
  Box,
  Stack,
  IconButton,
} from '@mui/material';
import DateRangeFilter from '../DateRangeFilter';
import { LightTooltip } from '../custom-components/custom-tooltip';
import SearchBar from '../../sections/@dashboard/SearchBar';
import Iconify from '../iconify';

const TRANSACTION_TYPE_CHOICES = [
  { value: 'All', label: 'All Types' },
  { value: 'Income', label: 'Income' },
  { value: 'Expense', label: 'Expense' },
  { value: 'Transfer', label: 'Transfer' },
];

const STATUS_CHOICES = [
  { value: 'All', label: 'All Status' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Failed', label: 'Failed' },
];

const FinanceFilters = ({
  searchTerm,
  transactionTypeFilter,
  statusFilter,
  dateRange,
  onSearchChange,
  onFilterChange,
  onDateRangeChange,
  onClearFilters,
}) => {
  return (
    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: (theme) => theme.customShadows?.z1 || theme.shadows[1] }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
            <SearchBar onSearch={(val) => onSearchChange({ target: { value: val } })} value={searchTerm} />
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
              <Autocomplete
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                value={
                  TRANSACTION_TYPE_CHOICES.find((type) => type.value === transactionTypeFilter) || {
                    value: 'All',
                    label: 'All Types',
                  }
                }
                onChange={(event, newValue) => {
                  onFilterChange('transactionType', newValue?.value || 'All');
                }}
                options={TRANSACTION_TYPE_CHOICES}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Type" />}
                clearOnEscape
              />
              <LightTooltip title="Filter transactions by type (income, expense, or transfer)." placement="top">
                <IconButton
                  size="small"
                  aria-label="Help: type filter"
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                  tabIndex={-1}
                >
                  <Iconify icon="eva:info-outline" width={18} />
                </IconButton>
              </LightTooltip>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
              <Autocomplete
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                value={
                  STATUS_CHOICES.find((status) => status.value === statusFilter) || {
                    value: 'All',
                    label: 'All Status',
                  }
                }
                onChange={(event, newValue) => {
                  onFilterChange('status', newValue?.value || 'All');
                }}
                options={STATUS_CHOICES}
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => <TextField {...params} label="Status" />}
                clearOnEscape
              />
              <LightTooltip title="Filter transactions by processing status." placement="top">
                <IconButton
                  size="small"
                  aria-label="Help: status filter"
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                  tabIndex={-1}
                >
                  <Iconify icon="eva:info-outline" width={18} />
                </IconButton>
              </LightTooltip>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={dateRange.preset === 'custom' ? 4 : 2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DateRangeFilter
                  onDateRangeChange={onDateRangeChange}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  selectedPreset={dateRange.preset}
                  sx={{ height: '40px', width: '100%' }}
                />
              </Box>
              <LightTooltip title="Limit results to activity within the selected period." placement="top">
                <IconButton
                  size="small"
                  aria-label="Help: date range filter"
                  sx={{ flexShrink: 0, color: 'text.secondary' }}
                  tabIndex={-1}
                >
                  <Iconify icon="eva:info-outline" width={18} />
                </IconButton>
              </LightTooltip>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={dateRange.preset === 'custom' ? 1 : 3}>
            <Button variant="outlined" onClick={onClearFilters} size="small" sx={{ height: '40px', width: '100%' }}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

FinanceFilters.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  transactionTypeFilter: PropTypes.string.isRequired,
  statusFilter: PropTypes.string.isRequired,
  dateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    preset: PropTypes.string,
  }).isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
};

export default FinanceFilters;
